import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, Card, CardContent, Chip,
  IconButton, Tooltip, TextField, FormControl, InputLabel, Select, MenuItem,
  useTheme, useMediaQuery,
} from '@mui/material';
import {
  CheckCircle, Cancel, Image as ImageIcon,
  Refresh, Person, CalendarToday, SwapHoriz, Payments, Storefront,
} from '@mui/icons-material';
import {
  adminGetWithdrawals, adminApproveWithdrawal, adminRejectWithdrawal,
  adminGetBankAccounts, adminVerifyBankAccount, adminRejectBankAccount,
  getBankPhotoUrl,
} from 'api/withdrawal';
import AuthenticatedImage from 'components/AuthenticatedImage';
import CashPayoutDialog from './CashPayoutDialog';
import notificationService from 'services/notificationService';
import PageShell from 'components/bento/PageShell';
import BentoCard from 'components/bento/BentoCard';
import BentoTabs from 'components/bento/BentoTabs';
import { user } from 'localStorage';
import { getVisibleTabs } from 'utils/pagePermissions';
import { useUserRefreshTick } from 'hooks/useUserRefreshTick';

const ALL_WITHDRAWAL_TABS = [
  { key: 'requests', slug: 'wallet/withdrawal/requests', label: 'Withdrawal Requests' },
  { key: 'bank', slug: 'wallet/withdrawal/bank-verification', label: 'Bank Account Verification' },
];

const CURRENCY_SYMBOLS = { USD: '$', THB: '฿', LAK: '₭' };
const fmt = (amount, currency) => `${CURRENCY_SYMBOLS[currency] || ''}${Number(amount || 0).toLocaleString()}`;
const fmtDate = (s) => new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const STATUS_COLORS = {
  pending: 'warning', approved: 'success', paid: 'success', rejected: 'error',
  cancelled: 'default', expired: 'default', verified: 'success',
};

const WithdrawalManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  useUserRefreshTick(); // re-render when the cached profile refreshes — see hooks/useUserRefreshTick.js

  const visibleSlugs = getVisibleTabs(user(), 'wallet/withdrawal').map((t) => t.slug);
  const tabs = ALL_WITHDRAWAL_TABS.filter((t) => visibleSlugs.includes(t.slug));
  // BentoTabs' value/onChange are index-based, so `tab` stays an index into the
  // filtered `tabs` array; activeKey is derived for rendering.
  const [tab, setTab] = useState(0);
  useEffect(() => {
    if (tab >= tabs.length) setTab(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.length]);
  const activeKey = tabs[tab]?.key;

  return (
    <PageShell title="Withdrawals">
      <BentoTabs
        tabs={tabs}
        value={tab}
        onChange={setTab}
        sx={{ display: 'inline-flex', mb: '22px' }}
      />

      {activeKey === 'requests' && <WithdrawalRequestsTab isMobile={isMobile} theme={theme} />}
      {activeKey === 'bank' && <BankVerificationTab isMobile={isMobile} theme={theme} />}
    </PageShell>
  );
};

// ─── Tab 1: Withdrawal Requests ──────────────────────────────────────────────
const WithdrawalRequestsTab = ({ isMobile, theme }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [currencyFilter, setCurrencyFilter] = useState('');

  const [selected, setSelected] = useState(null);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  // Cash payout dialog: { request } for a list row, {} to look up by code.
  const [payout, setPayout] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (currencyFilter) filters.currency = currencyFilter;
      const result = await adminGetWithdrawals(filters);
      setRequests(result.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load withdrawal requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, currencyFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => notificationService.subscribe('notifications-loaded', load), [load]);

  const handleApprove = async () => {
    if (!selected) return;
    try {
      setActionLoading(true);
      setError('');
      await adminApproveWithdrawal(selected._id, approvalNotes);
      setSuccess(`Withdrawal ${selected._id} approved`);
      setApproveDialog(false);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selected || !rejectionReason.trim()) { setError('Rejection reason is required'); return; }
    try {
      setActionLoading(true);
      setError('');
      await adminRejectWithdrawal(selected._id, rejectionReason);
      setSuccess(`Withdrawal ${selected._id} rejected`);
      setRejectDialog(false);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  // Cash requests: where it will be / was collected. Bank (legacy): the account.
  const destination = (r) => {
    if (r.method === 'cash') {
      if (r.status === 'paid') {
        return [`Cash paid at ${r.paidStation?.name || r.paidStationId || '—'}`, `by ${r.paidBy?.name || '—'}${r.paidAt ? ` · ${fmtDate(r.paidAt)}` : ''}`];
      }
      return ['Cash at branch', `Customer chose ${r.station?.name || r.stationId || '—'}`];
    }
    return [`${r.bankAccount?.bankCode || ''} · ${r.bankAccount?.currency || ''}`, r.bankAccount?.accountNumber || ''];
  };

  const actionButtons = (r, size) => (
    r.method === 'cash' ? (
      <>
        <Button size={size} variant="contained" color="success" startIcon={<Payments />} onClick={() => setPayout({ request: r })}>Pay out</Button>
        <Button size={size} variant="contained" color="error" startIcon={<Cancel />} onClick={() => { setSelected(r); setRejectionReason(''); setRejectDialog(true); }}>Reject</Button>
      </>
    ) : (
      <>
        <Button size={size} variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => { setSelected(r); setApprovalNotes(''); setApproveDialog(true); }}>Approve</Button>
        <Button size={size} variant="contained" color="error" startIcon={<Cancel />} onClick={() => { setSelected(r); setRejectionReason(''); setRejectDialog(true); }}>Reject</Button>
      </>
    )
  );

  const payoutLabel = (r) => {
    if (r.isConverted) {
      return `${fmt(r.sourceAmount, r.sourceCurrency)} → ${fmt(r.payoutAmount, r.payoutCurrency)}`;
    }
    return fmt(r.sourceAmount, r.sourceCurrency);
  };

  return (
    <Box>
      {/* Filters */}
      <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center" mb={2}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="paid">Paid (cash)</MenuItem>
            <MenuItem value="approved">Approved (bank)</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Currency</InputLabel>
          <Select value={currencyFilter} label="Currency" onChange={(e) => setCurrencyFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="LAK">LAK</MenuItem>
            <MenuItem value="THB">THB</MenuItem>
            <MenuItem value="USD">USD</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" color="secondary" size="small" startIcon={<Refresh />} onClick={load} disabled={loading}>
          Refresh
        </Button>
        <Button variant="contained" color="secondary" size="small" startIcon={<Payments />} onClick={() => setPayout({})}>
          Pay by code
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress color="secondary" /></Box>
      ) : requests.length === 0 ? (
        <BentoCard bodySx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No withdrawal requests</Typography>
        </BentoCard>
      ) : isMobile ? (
        <Grid container spacing={2}>
          {requests.map((r) => (
            <Grid item xs={12} key={r._id}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" color="secondary.main" fontWeight={700}>{payoutLabel(r)}</Typography>
                    <Chip size="small" label={r.status} color={STATUS_COLORS[r.status] || 'default'} />
                  </Box>
                  {r.isConverted && <Chip size="small" icon={<SwapHoriz />} label="Converted" variant="outlined" sx={{ mb: 1 }} />}
                  <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
                    <Person fontSize="small" color="action" />
                    <Typography variant="body2">{r.customer?.name || '—'} · {destination(r).join(' · ')}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.75} mb={1}>
                    <CalendarToday fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">{fmtDate(r.createdAt)}</Typography>
                  </Box>
                  {r.status === 'pending' && (
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                      {actionButtons(r, 'medium')}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <BentoCard bodySx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Amount → Payout</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r._id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">{r.customer?.name || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">{r.customer?.phone}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">{payoutLabel(r)}</Typography>
                      {r.isConverted && <Chip size="small" icon={<SwapHoriz />} label="Converted" variant="outlined" sx={{ mt: 0.5 }} />}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {r.method === 'cash' && <Storefront fontSize="small" color="action" />}
                        <Typography variant="body2">{destination(r)[0]}</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">{destination(r)[1]}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2">{fmtDate(r.createdAt)}</Typography></TableCell>
                    <TableCell><Chip size="small" label={r.status} color={STATUS_COLORS[r.status] || 'default'} /></TableCell>
                    <TableCell>
                      {r.status === 'pending' ? (
                        <Box display="flex" gap={1}>
                          {actionButtons(r, 'small')}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          {r.reviewedBy?.name ? `by ${r.reviewedBy.name}` : r.paidBy?.name ? `by ${r.paidBy.name}` : '—'}
                          {r.rejectionReason ? ` · ${r.rejectionReason}` : ''}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </BentoCard>
      )}

      {/* Approve dialog */}
      <Dialog open={approveDialog} onClose={() => setApproveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}><CheckCircle color="success" sx={{ mr: 1 }} />Approve Withdrawal</DialogTitle>
        <DialogContent>
          {selected && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Customer: {selected.customer?.name}</Typography>
              <Typography variant="body2" color="text.secondary">Withdraw: {fmt(selected.sourceAmount, selected.sourceCurrency)}</Typography>
              <Typography variant="body2" color="text.secondary">Pay out: {fmt(selected.payoutAmount, selected.payoutCurrency)} to {selected.bankAccount?.bankCode} {selected.bankAccount?.accountNumber}</Typography>
              {selected.isConverted && <Typography variant="caption" color="warning.main">Converted at locked rate</Typography>}
            </Box>
          )}
          <TextField fullWidth label="Approval Notes (Optional)" multiline rows={2} value={approvalNotes} onChange={(e) => setApprovalNotes(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(false)} variant="outlined" color="secondary">Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={actionLoading} startIcon={actionLoading ? <CircularProgress size={18} /> : <CheckCircle />}>
            {actionLoading ? 'Approving...' : 'Confirm Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      <CashPayoutDialog
        open={!!payout}
        request={payout?.request || null}
        onClose={() => setPayout(null)}
        onPaid={async (msg) => { setSuccess(msg); await load(); }}
      />

      {/* Reject dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}><Cancel color="error" sx={{ mr: 1 }} />Reject Withdrawal</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Rejection Reason" multiline rows={3} value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Reason for rejecting this withdrawal..." sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)} variant="outlined" color="secondary">Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={actionLoading || !rejectionReason.trim()} startIcon={actionLoading ? <CircularProgress size={18} /> : <Cancel />}>
            {actionLoading ? 'Rejecting...' : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Tab 2: Bank Account Verification ────────────────────────────────────────
const BankVerificationTab = ({ isMobile, theme }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');

  const [selected, setSelected] = useState(null);
  const [photoDialog, setPhotoDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      const result = await adminGetBankAccounts(filters);
      setAccounts(result.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => notificationService.subscribe('notifications-loaded', load), [load]);

  const handleVerify = async (acc) => {
    try {
      setActionLoading(true);
      setError('');
      await adminVerifyBankAccount(acc._id);
      setSuccess(`Bank account verified`);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to verify');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selected || !rejectionReason.trim()) { setError('Rejection reason is required'); return; }
    try {
      setActionLoading(true);
      setError('');
      await adminRejectBankAccount(selected._id, rejectionReason);
      setSuccess('Bank account rejected');
      setRejectDialog(false);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center" mb={2}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="verified">Verified</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" color="secondary" size="small" startIcon={<Refresh />} onClick={load} disabled={loading}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress color="secondary" /></Box>
      ) : accounts.length === 0 ? (
        <BentoCard bodySx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No bank accounts</Typography>
        </BentoCard>
      ) : (
        <BentoCard bodySx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell>Bank</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Holder</TableCell>
                  <TableCell>Photo</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((a) => (
                  <TableRow key={a._id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">{a.customer?.name || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">{a.customer?.phone}</Typography>
                    </TableCell>
                    <TableCell><Chip size="small" label={a.currency} variant="outlined" /></TableCell>
                    <TableCell><Typography variant="body2">{a.bankCode}</Typography></TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{a.accountNumber}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{a.accountHolderName}</Typography></TableCell>
                    <TableCell>
                      {a.bookPhoto?.filename && (
                        <Tooltip title="View bank book photo">
                          <IconButton size="small" onClick={() => { setSelected(a); setPhotoDialog(true); }}><ImageIcon /></IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell><Chip size="small" label={a.verificationStatus} color={STATUS_COLORS[a.verificationStatus] || 'default'} /></TableCell>
                    <TableCell>
                      {a.verificationStatus === 'pending' ? (
                        <Box display="flex" gap={1}>
                          <Button size="small" variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => handleVerify(a)} disabled={actionLoading}>Verify</Button>
                          <Button size="small" variant="contained" color="error" startIcon={<Cancel />} onClick={() => { setSelected(a); setRejectionReason(''); setRejectDialog(true); }}>Reject</Button>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled">{a.rejectionReason || (a.reviewedBy?.name ? `by ${a.reviewedBy.name}` : '—')}</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </BentoCard>
      )}

      {/* Photo dialog */}
      <Dialog open={photoDialog} onClose={() => setPhotoDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bank Book — {selected?.accountHolderName}</DialogTitle>
        <DialogContent>
          {selected?.bookPhoto?.filename && (
            <AuthenticatedImage
              src={getBankPhotoUrl(selected.bookPhoto.filename)}
              alt="Bank book"
              style={{ width: '100%', height: 'auto', maxHeight: '70vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoDialog(false)} variant="outlined" color="secondary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}><Cancel color="error" sx={{ mr: 1 }} />Reject Bank Account</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Rejection Reason" multiline rows={3} value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="e.g. blurry photo, account number mismatch..." sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)} variant="outlined" color="secondary">Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={actionLoading || !rejectionReason.trim()} startIcon={actionLoading ? <CircularProgress size={18} /> : <Cancel />}>
            {actionLoading ? 'Rejecting...' : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WithdrawalManagement;
